// The post-visit rating link (/me/rate/<token>) and its JSON endpoints
// (/me/rating-invitations/<token>[/rating]) carry a bearer credential in the URL
// path (docs/patient-ratings.md). Bugsnag starts in every bundle — including on
// the rating page — and its XHR/navigation breadcrumbs and captured request URL
// would otherwise ship the token with any error report. These helpers are wired
// into Bugsnag.start in errorHandling.js.
//
// Kept separate from analytics.js (whose redactRatingTokenURL covers the PostHog
// page-URL shape only) so importing them doesn't pull posthog-js into every
// bundle that uses errorHandling.

// Tokens are 40 URL-safe base64 chars today; match 20+ like the PostHog
// sanitizer so a future length change stays covered. A path segment ends at "/",
// so trailing segments like "/rating" survive the replacement.
import { redactStripeClientSecrets } from "./stripeSecretRedaction";

const RATING_TOKEN_PATH_RE =
  /(\/me\/(?:rate|rating-invitations)\/)[A-Za-z0-9_-]{20,}/g;

export function redactRatingTokenText(value) {
  if (typeof value !== "string") {
    return value;
  }
  return redactStripeClientSecrets(value).replace(
    RATING_TOKEN_PATH_RE,
    "$1[redacted]",
  );
}

// Bugsnag onBreadcrumb callback: scrubs the message and any string metadata
// value (XHR breadcrumbs put "GET <url>" in metadata.request; navigation
// breadcrumbs put URLs in metadata.from / metadata.to). Mutates in place, as the
// Bugsnag callback API expects; never discards the breadcrumb.
export function redactRatingTokenFromBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== "object") {
    return;
  }
  breadcrumb.message = redactRatingTokenText(breadcrumb.message);
  const metadata = breadcrumb.metadata;
  if (metadata && typeof metadata === "object") {
    Object.keys(metadata).forEach((key) => {
      metadata[key] = redactRatingTokenText(metadata[key]);
    });
  }
}

// Bugsnag onError callback half: scrubs the URL-ish fields Bugsnag captures at
// error time (request.url / request.referer, context). Breadcrumbs are already
// scrubbed at creation by redactRatingTokenFromBreadcrumb, but sweep them again
// in case a plugin attached some directly.
export function redactRatingTokenFromEvent(event) {
  if (!event || typeof event !== "object") {
    return;
  }
  if (event.request && typeof event.request === "object") {
    event.request.url = redactRatingTokenText(event.request.url);
    event.request.referer = redactRatingTokenText(event.request.referer);
  }
  event.context = redactRatingTokenText(event.context);
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs.forEach(redactRatingTokenFromBreadcrumb);
  }
}
